<script>
  import { onMount, onDestroy } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import { fetchDeliveries } from '../../controllers/deliveryController'; 
  import { fetchTransporte } from '../../controllers/transporteController';
  import { fetchVehiculos } from '../../controllers/vehiculoController';
  import { fetchEscoltas } from '../../controllers/escoltaController';
  import { fetchCorredores } from '../../controllers/corredorController';
  import DeliveryDetails from './DeliveryDetails.svelte';
  import DeliveryMap from './DeliveryMap.svelte';
  import DefineDelivery from './DefineDelivery.svelte';
  import Gallery from './Gallery.svelte';
  import { getEgresoDets } from '../../models/egresoDetModel';
  import { getStatusPaneTheme } from '../../config.js';
  import { portal } from '../../lib/portal.js';
  import { paneSlide } from '../../lib/paneTransition.js';

  const dispatch = createEventDispatcher();

  export let selectedItem;
  export let closePane;
  export let status;

  let activeTab = 'details';
  let deliveries = [];
  let transportes = [];
  let vehiculos = [];
  let escoltas = [];
  let corredores = [];
  let refreshInterval;

  async function refreshEgresoDet() {
    if (selectedItem) {
      const egresoDets = await getEgresoDets(selectedItem.id, 1, 100);
      if (egresoDets) {
        selectedItem.egresoDet = egresoDets.items;
      }
    }
  }

  onMount(async () => {
    const deliveriesResult = await fetchDeliveries(1, 100);
    if (deliveriesResult) {
      deliveries = deliveriesResult.delivery;
    }

    const transporteResult = await fetchTransporte(1, 100);
    if (transporteResult) {
      transportes = transporteResult.transporte;
    }

    const vehiculoResult = await fetchVehiculos(1, 100);
    if (vehiculoResult) {
      vehiculos = vehiculoResult.vehiculo;
    }

    const escoltaResult = await fetchEscoltas(1, 100);
    if (escoltaResult) {
      escoltas = escoltaResult.escolta;
    }

    const corredorResult = await fetchCorredores(1, 100);
    if (corredorResult) {
      corredores = corredorResult.corredor;
    }

    await refreshEgresoDet();
    refreshInterval = setInterval(refreshEgresoDet, 2000);
  });

  onDestroy(() => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
  });

  $: paneStatus = selectedItem?.sta || status;
  $: paneTheme = getStatusPaneTheme(paneStatus);
  $: paneStyle = `--pane-bg: ${paneTheme.bg}; --pane-border: ${paneTheme.border}; --pane-text: ${paneTheme.text};`;
</script>

{#if selectedItem}
  <div use:portal class="status-pane-root">
    <div
      in:paneSlide
      out:paneSlide
      class="status-pane"
      style={paneStyle}
    >
      <div class="status-pane__header">
        <button
          type="button"
          class="status-pane__close"
          on:click={closePane}
          aria-label="Cerrar panel"
        >
          ✕
        </button>

        <nav class="status-pane__tabs" aria-label="Tabs">
          <button
            type="button"
            class={`status-pane__tab ${activeTab === 'details' ? 'status-pane__tab--active' : ''}`}
            on:click={() => activeTab = 'details'}
          >
            Detalles del Pedido
          </button>
          {#if selectedItem.sta === 'DELIVERED'}
            <button
              type="button"
              class={`status-pane__tab ${activeTab === 'delivery-map' ? 'status-pane__tab--active' : ''}`}
              on:click={() => activeTab = 'delivery-map'}
            >
              Ubicación de Entrega
            </button>
          {/if}
          {#if selectedItem.sta === 'NOT_READY' || selectedItem.sta === 'PARTIALLY_READY' || selectedItem.sta === 'READY' || selectedItem.sta === 'PACKING'}
            <button
              type="button"
              class={`status-pane__tab ${activeTab === 'define-delivery' ? 'status-pane__tab--active' : ''}`}
              on:click={() => activeTab = 'define-delivery'}
            >
              Método de Entrega
            </button>
          {/if}
          {#if selectedItem.sta === 'DELIVERED'}
            <button
              type="button"
              class={`status-pane__tab ${activeTab === 'gallery' ? 'status-pane__tab--active' : ''}`}
              on:click={() => activeTab = 'gallery'}
            >
              Galería
            </button>
          {/if}
        </nav>
      </div>

      <div class="status-pane__body">
        {#if activeTab === 'details'}
          <DeliveryDetails
            {selectedItem}
            paneStatus={paneStatus}
            on:pickerUpdated={(event) => dispatch('pickerUpdated', event.detail)}
          />
        {:else if activeTab === 'delivery-map'}
          <DeliveryMap {selectedItem} {deliveries} {transportes} {vehiculos} {escoltas} />
        {:else if activeTab === 'define-delivery'}
          <DefineDelivery {selectedItem} {deliveries} {transportes} {vehiculos} {escoltas} {dispatch} {closePane} />
        {:else if activeTab === 'gallery'}
          <Gallery {selectedItem} />
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .status-pane-root {
    position: fixed;
    inset: 0;
    z-index: 9999;
    pointer-events: none;
  }

  .status-pane {
    pointer-events: auto;
    position: fixed;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--pane-bg);
    border-left: none;
    color: var(--pane-text);
    box-shadow: none;
    opacity: 1;
    will-change: transform;
  }

  .status-pane__header {
    flex-shrink: 0;
    padding: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .status-pane__close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    color: inherit;
    opacity: 0.7;
    font-size: 1.25rem;
    line-height: 1;
  }

  .status-pane__close:hover {
    opacity: 1;
    color: #dc2626;
  }

  .status-pane__tabs {
    display: flex;
    overflow-x: auto;
    gap: 0.25rem;
    margin-top: 0.5rem;
    padding-bottom: 0.25rem;
  }

  .status-pane__tab {
    flex-shrink: 0;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-bottom: 2px solid transparent;
    color: inherit;
    opacity: 0.75;
  }

  .status-pane__tab:hover {
    opacity: 1;
  }

  .status-pane__tab--active {
    opacity: 1;
    border-bottom-color: currentColor;
    font-weight: 600;
  }

  .status-pane__body {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  @media (max-width: 768px) {
    .status-pane {
      width: 100%;
    }
  }
</style>
